<template>
  <div class="scroll_flex_container">
    <navbar title="集點商城"></navbar>

    <!-- 未登入遮罩層 -->
    <div class="unlogin-cover h-full w-full" v-if="!isLogin">
      <levitateLoginCard page="point" />
    </div>

    <!-- Loading 狀態 -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <p class="loading-text">載入中...</p>
    </div>

    <!-- Error 狀態 -->
    <div v-else-if="errorMessage" class="error-container">
      <div class="error-card">
        <i class="error-icon">⚠️</i>
        <p class="error-text">{{ errorMessage }}</p>
        <button class="retry-btn" @click="retryFetch">重試</button>
      </div>
    </div>

    <!-- 集點資訊卡片 -->
    <div v-else-if="campaignInfo" class="point-info-card">
      <!-- 現有集點 -->
      <div class="points-display">
        <div class="points-content">
          <p class="label">現有集點</p>
          <h1 class="points-number">{{ currentPoints }}</h1>
        </div>
        <div class="qrcode-section">
          <QRCode :value="memberQrCode" :size="100" level="H" renderAs="svg" />
        </div>
      </div>

      <!-- 活動資訊 -->
      <div class="campaign-info">
        <h3 class="campaign-name">{{ campaignInfo.campaignName }}</h3>
        <p class="campaign-type" :class="`mode-${campaignInfo.mode}`">
          活動類型：{{ campaignInfo.campaignType }}
        </p>
        <p class="campaign-date">
          活動日期：{{ campaignInfo.startDate }}-{{ campaignInfo.endDate }}
        </p>
      </div>

      <!-- 分隔線 -->
      <div class="divider"></div>

      <!-- 活動說明 -->
      <p class="description">{{ campaignInfo.description }}</p>

      <!-- 注意事項 -->
      <div class="notice">
        <div class="notice-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
  <circle cx="12" cy="12" r="10" fill="none" stroke="#D60C0A" stroke-width="2"/>
  <rect x="11" y="7" width="2" height="6" fill="#D60C0A"/>
  <circle cx="12" cy="16" r="1" fill="#D60C0A"/>
</svg>
        </div>
        <p class="notice-text">{{ campaignInfo.notice }}</p>
      </div>

      <!-- 查看兌換商品按鈕 -->
      <button class="exchange-btn" @click="goToExchangePage">
        查看兌換商品
      </button>
    </div>

    <!-- Empty 狀態 -->
    <div v-else class="empty-state">
      <text class="material-outlined empty-icon">scatter_plot</text>
      <p class="empty-text">目前沒有集點活動</p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import navbar from '@/components/general/navbar.vue';
import levitateLoginCard from '@/components/home/levitateLoginCard.vue';
import commonUtils from '@/utils/common.utils';
import auth from '@/utils/auth.utils';
import QRCode from 'qrcode.vue';

export default {
  name: 'PointPage',
  
  components: {
    navbar,
    levitateLoginCard,
    QRCode
  },
  
  computed: {
    ...mapGetters('point', ['currentPoints', 'campaignInfo', 'isLoading', 'errorMessage']),
    ...mapGetters('userData', {
      isLogin: 'isLogin',
      accessToken: 'accessToken',
      storeCode: 'storeCode',
      isStoreMember: 'isStoreMember',
      memberQrCode: 'memberQrCode'
    }),
    ...mapState('basic', {
      currentStore: 'currentStore'
    }),
    ...mapGetters('basic', {
      errorLoginState: 'errorLoginState',
      errorLoginMessage: 'errorLoginMessage'
    }),
    ...mapGetters('line', {
      lineAccessToken: 'accessToken'
    })
  },
  
  watch: {
    async errorLoginState(state) {
      if (state) {
        commonUtils.closeLoading();
        let errorMessage = this.errorLoginMessage;
        if (errorMessage === 'IdToken expired.') {
          errorMessage = '登入狀態已過期，請重新登入';
        }
        if (errorMessage === 'No Such Store') {
          errorMessage = '查無此店家';
        }
        commonUtils.createToast("error", errorMessage);
        auth.logout();
      }
    }
  },
  
  methods: {
    ...mapActions('point', ['fetchPointInfo', 'fetchMemberTotalPoints']),
    
    /**
     * 初始化
     */
    async init() {
      let loginProcess = false;
      
      // 如果有 LINE access token 則進行 okShopLogin
      if (!this.isLogin && this.lineAccessToken) {
        loginProcess = true;
        try {
          await auth.okShopLogin();
        } catch (e) {
          console.error("Failed to login gen2 pos system:", e);
          this.$store.dispatch("basic/setErrorLogin", { state: true, message: e });
          commonUtils.closeLoading();
          commonUtils.createToast("error", "登入失敗，請重新登入");
          return;
        }
      } else if (!this.accessToken) {
        // 如果沒有 okshop access token || line access token 則不進行任何動作
        return;
      }

      try {
        // 如果不是該商店會員 則進行加入會員
        if (!this.isStoreMember) {
          await this.$store.dispatch('userData/joinMember');
        }
        
        // 載入會員資訊和商店資訊
        await this.$store.dispatch('userData/fetchMemberInfo');
        await this.$store.dispatch('basic/fetchStoreInfo');
        
        // 會員點數需要依照活動日期判斷，需先取得活動資訊再計算點數
        await this.fetchPointInfo();
        await this.fetchMemberTotalPoints();
        
        if (loginProcess) {
          commonUtils.closeLoading();
          commonUtils.createToast("success", "登入成功", "");
        }
      } catch (e) {
        console.error(e);
        commonUtils.closeLoading();
        commonUtils.createToast("error", "資料載入失敗");
      }
    },
    
    /**
     * 重試獲取資料
     */
    async retryFetch() {
      await this.init();
    },
    
    /**
     * 前往兌換商品頁面
     */
    goToExchangePage() {
      const currentStore = this.$store.state.basic.currentStore;
      this.$router.push(`/${currentStore}/point/exchange`);
    }
  },
  
  async created() {
    this.$store.dispatch("basic/setErrorLogin", { state: false, message: '' });
    await this.init();
  }
};
</script>

<style lang="scss" scoped>
.scroll_flex_container {
  min-height: 100vh;
  background-color: #F0F0F0;
}

// Loading 狀態
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 52px); // 扣除 navbar 高度
  padding: 40px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #F0F0F0;
  border-top-color: #FD5001;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
  color: #666;
  letter-spacing: 0.64px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Error 狀態
.error-container {
  padding: 40px 20px;
}

.error-card {
  background: white;
  padding: 40px 20px;
  border-radius: 8px;
  text-align: center;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-text {
  color: #D60C0A;
  font-size: 16px;
  margin-bottom: 20px;
}

.retry-btn {
  background: #FD5001;
  color: white;
  border: none;
  padding: 10px 30px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  
  &:hover {
    background: #E64801;
  }
}

// 集點資訊卡片
.point-info-card {
  background: white;
  margin: 16px;
  padding: 30px 20px;
  border-radius: 8px;
}

.points-display {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  padding: 0 10px;
  
  .points-content {
    flex: 0 0 auto;
    text-align: center;
    margin-right: 20px;
  }
  
  .qrcode-section {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
  }
  
  .label {
    font-size: 20px;
    font-weight: 500;
    color: #000;
    margin-bottom: 8px;
  }
  
  .points-number {
    font-size: 64px;
    font-weight: 700;
    color: #FD5001;
    margin: 0;
    line-height: 1.2;
  }
}

.campaign-info {
  margin-bottom: 16px;
  
  .campaign-name {
    font-size: 22px;
    font-weight: 500;
    color: #000;
    text-align: center;
    margin-bottom: 12px;
    letter-spacing: 0.88px;
  }
  
  .campaign-type {
    font-size: 16px;
    margin-bottom: 8px;
    letter-spacing: 0.64px;
    text-align: center;
    
    // mode=1（滿額集點）：粉紅色
    &.mode-1 {
      color: #F50B53;
    }
    
    // mode=2（特定商品集點）：青綠色
    &.mode-2 {
      color: #02B5A8;
    }
  }
  
  .campaign-date {
    font-size: 16px;
    color: #666;
    letter-spacing: 0.64px;
    text-align: center;
  }
}

.divider {
  border-top: 1px dashed #000;
  margin: 24px 0;
}

.description {
  font-size: 16px;
  color: #666;
  line-height: 24px;
  margin-bottom: 16px;
  letter-spacing: 0.64px;
}

.notice {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 24px;
  
  .notice-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }
  
  .notice-text {
    flex: 1;
    color: #D60C0A;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.64px;
    margin: 0;
  }
}

.exchange-btn {
  width: 100%;
  height: 46px;
  border: 1px solid #1E1E1E;
  background: white;
  color: #000;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  letter-spacing: 0.64px;
  transition: all 0.3s;
  
  &:hover {
    background: #F5F5F5;
  }
  
  &:active {
    background: #E8E8E8;
  }
}

// Empty 狀態
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 52px); // 扣除 navbar 高度
  padding: 40px 20px;
  
  .empty-icon {
    font-size: 120px;
    color: #AAAAAA;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .empty-text {
    font-size: 18px;
    color: #AAAAAA;
    margin: 0;
    letter-spacing: 0.72px;
  }
}

// 未登入遮罩層
.unlogin-cover {
  position: fixed;
  top: 52px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
</style>
